/**
 * Utility functions for handling share functionality
 * Supports native Web Share API for mobile and fallback for desktop
 */

interface ShareData {
  title: string;
  text?: string;
  url?: string;
}

/**
 * Check if native Web Share API is available and can share the data
 */
const canUseNativeShare = (data: ShareData): boolean => {
  if (!navigator.share) {
    return false;
  }

  // Check if the browser can share this type of data
  if (navigator.canShare && !navigator.canShare(data)) {
    return false;
  }

  return true;
};

/**
 * Get the current page URL reliably
 * Constructs URL from window.location parts to ensure accuracy
 */
export const getCurrentUrl = (): string => {
  // Construct URL from parts to ensure we get the correct URL
  const { protocol, host, pathname, search, hash } = window.location;
  return `${protocol}//${host}${pathname}${search}${hash}`;
};

/**
 * Share using native Web Share API
 */
const nativeShare = async (data: ShareData): Promise<void> => {
  try {
    await navigator.share(data);
  } catch (error) {
    // User cancelled or error occurred
    console.log('Share cancelled or failed:', error);
    throw error;
  }
};

/**
 * Fallback share for specific platforms
 */
const fallbackShare = (platform: string, data: ShareData): void => {
  const url = encodeURIComponent(data.url || getCurrentUrl());
  const text = encodeURIComponent(data.text || data.title);

  let shareUrl = '';

  switch (platform) {
    case 'facebook':
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      break;
    case 'twitter':
      shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
      break;
    case 'linkedin':
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
      break;
    case 'whatsapp':
      shareUrl = `https://wa.me/?text=${text}%20${url}`;
      break;
    default:
      console.warn(`Unknown share platform: ${platform}`);
      return;
  }

  if (shareUrl) {
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
  }
};

/**
 * Main share function that handles both native and fallback sharing
 *
 * @param platform - The platform to share to (for fallback) or 'native' for native sharing
 * @param data - The data to share (title, text, url)
 */
export const handleShare = async (
  platform: string,
  data: ShareData
): Promise<void> => {
  // Ensure we have a URL
  const shareData: ShareData = {
    ...data,
    url: data.url || getCurrentUrl()
  };

  // Try native share first if requested or on mobile
  if (platform === 'native' || (platform === 'auto' && isMobileDevice())) {
    if (canUseNativeShare(shareData)) {
      try {
        await nativeShare(shareData);
        return;
      } catch (error) {
        // Fall through to fallback if native share fails
        console.log('Native share failed, using fallback');
      }
    }
  }

  // Use fallback share for specific platform
  if (platform !== 'native' && platform !== 'auto') {
    fallbackShare(platform, shareData);
  }
};

/**
 * Detect if the user is on a mobile device
 */
export const isMobileDevice = (): boolean => {
  // Check for mobile user agents
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  // Check both user agent and touch capability
  return mobileRegex.test(navigator.userAgent) ||
         ('ontouchstart' in window) ||
         (navigator.maxTouchPoints > 0);
};

/**
 * Check if native share is supported
 */
export const isNativeShareSupported = (): boolean => {
  return !!navigator.share;
};